import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import { DiscoverGameGamePlayerResponse, DiscoverGameResponse } from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { AgeCategoryOptions, BooleanChoices, GameAnalysisStatusEnumChoices } from "features/discover/types";
import { renderBoolean, renderBooleanChoiceLabel } from "features/datagrid-configs/cellRenderers";
import { createSortArrows, formatDate, formatDateTime, humanizeLabel, ucFirst } from "features/discover/functions";
import { Link } from "react-router";
import { DateTimeRangeCalendar } from "ui-kit/src/DateTimeRangeCalendar/DateTimeRangeCalendar";
import { useI18n } from "features/translation/context";
import Modal from "../../../../../assets/js/components/Modal";
import { DiscoverGameAnalysisDialog } from "features/discover/DiscoverGameAnalysisDialog";
import { useToast } from "ui-kit";
import { isGranted } from "pages/productManagement/functions";
import { Permission, Resource } from "pages/productManagement/types";

const processingLevelOptions = [
	{
		label: "Full",
		value: "full",
	},
	{
		label: "Limited",
		value: "limited",
	},
];

const isDiscoverOptions = [
	{
		label: "Discover",
		value: "true",
	},
	{
		label: "Elite",
		value: "false",
	},
];

const providerTagOptions = [
	{
		label: "ANY",
		value: "ANY",
	},
	{
		label: "BEPRO",
		value: "BEPRO",
	},
	{
		label: "KORASTATS",
		value: "KORASTATS",
	},
	{
		label: "SPORTLOGIQ",
		value: "SPORTLOGIQ",
	},
	{
		label: "UBITRACK",
		value: "UBITRACK",
	},
	{
		label: "NONE",
		value: "NONE",
	},
];

export function DiscoverGamesTable({
	columnVisibility = {},
	defaultFilters = {},
	columnsVisibilityKey = "columnsVisibility",
}: {
	columnVisibility?: Record<string, boolean>;
	defaultFilters?: { [key: string]: string | number | undefined };
	columnsVisibilityKey?: string;
}) {
	const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
		pageIndex: 0,
		pageSize: 10,
	});

	const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({ ...defaultFilters });
	const [filterValues, setFilterValues] = useState<{ [key: string]: string | number | undefined }>({ ...defaultFilters });
	const [sort, setSort] = useState<{ field: string; direction: string }>();

	const [dateTimeRange, setDateTimeRange] = useState({ start: "", end: "" });
	const [analysisRequestedAtTimeRange, setAnalysisRequestedAtTimeRange] = useState({ start: "", end: "" });

	const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(columnVisibility);
	const [visibleColumnsTick, setVisibleColumnsTick] = useState<boolean>(false);
	const [showColumnsModal, setShowColumnsModal] = useState<boolean>(false);
	const { locale } = useI18n();

	const [gameAnalysisInEdit, setGameAnalysisInEdit] = useState<DiscoverGameResponse | null>(null);

	const { addSuccessToast, addErrorToast } = useToast();
	const patchDiscoverGameMutation = client.patchDiscoverGame.useMutation({
		onSuccess: () => {
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverGamesTable") || query.queryKey.includes("discoverGameDetail"),
			});
			addSuccessToast(_("discover.game.field_updated_success"));
		},
		onError: () => {
			addErrorToast(_("discover.game.field_updated_error"));
		},
	});
	const canEditVip = isGranted(Resource.GAME_UPLOAD_SINGLE, Permission.GAME_VIP);

	useEffect(() => {
		setScrollBehavior();
	}, []);

	useEffect(() => {
		const memory = JSON.parse(localStorage.getItem(columnsVisibilityKey) || "{}");

		setVisibleColumns({
			...columnVisibility,
			...memory,
		});
		localStorage.setItem(
			columnsVisibilityKey,
			JSON.stringify({
				...columnVisibility,
				...memory,
			})
		);
	}, []);

	const { data, isLoading } = client.getDiscoverGames.useQuery(
		{
			page: pagination.pageIndex + 1,
			limit: pagination.pageSize,
			filters: [
				...(filters?.id
					? [
							{
								field: "id",
								type: "search",
								value: filters.id,
							},
						]
					: []),
				...(filters?.ageCategory
					? [
							{
								field: "ageCategory",
								type: "exact",
								value: filters.ageCategory,
							},
						]
					: []),
				...(filters?.anyTeamId
					? [
							{
								field: "anyTeamId",
								type: "exact",
								value: filters.anyTeamId,
							},
						]
					: []),
				...(filters?.anyClubId
					? [
							{
								field: "anyClubId",
								type: "exact",
								value: filters.anyClubId,
							},
						]
					: []),
				...(filters?.homeClub
					? [
							{
								field: "homeClub",
								type: "search",
								value: filters.homeClub,
							},
						]
					: []),
				...(filters?.homeTeam
					? [
							{
								field: "homeTeam",
								type: "search",
								value: filters.homeTeam,
							},
						]
					: []),
				...(filters?.awayClub
					? [
							{
								field: "awayClub",
								type: "search",
								value: filters.awayClub,
							},
						]
					: []),
				...(filters?.awayTeam
					? [
							{
								field: "awayTeam",
								type: "search",
								value: filters.awayTeam,
							},
						]
					: []),
				...(filters?.owner
					? [
							{
								field: "owner",
								type: "exact",
								value: filters.owner,
							},
						]
					: []),
				...(filters?.providerTag
					? [
							{
								field: "providerTag",
								type: "exact",
								value: filters.providerTag,
							},
						]
					: []),
				...(filters?.isDiscover
					? [
							{
								field: "isDiscover",
								type: "boolean",
								value: filters.isDiscover === "true",
							},
						]
					: []),
				...(filters?.isVip
					? [
							{
								field: "isVip",
								type: "boolean",
								value: filters.isVip === "true",
							},
						]
					: []),
				...(filters?.isHidden
					? [
							{
								field: "isHidden",
								type: "boolean",
								value: filters.isHidden === "true",
							},
						]
					: []),
				...(filters?.isHiddenForScout
					? [
							{
								field: "isHiddenForScout",
								type: "boolean",
								value: filters.isHiddenForScout === "true",
							},
						]
					: []),
				...(filters?.hasVideo
					? [
							{
								field: "hasVideo",
								type: "boolean",
								value: filters.hasVideo === "true",
							},
						]
					: []),
				...(dateTimeRange.start || dateTimeRange.end
					? [
							{
								field: "date",
								type: "datetimerange",
								value: {
									start: dateTimeRange.start === "" ? undefined : dateTimeRange.start,
									end: dateTimeRange.end === "" ? undefined : dateTimeRange.end,
								},
							},
						]
					: []),
				...(analysisRequestedAtTimeRange.start || analysisRequestedAtTimeRange.end
					? [
							{
								field: "analysisRequestedAt",
								type: "datetimerange",
								value: {
									start: analysisRequestedAtTimeRange.start === "" ? undefined : analysisRequestedAtTimeRange.start,
									end: analysisRequestedAtTimeRange.end === "" ? undefined : analysisRequestedAtTimeRange.end,
								},
							},
						]
					: []),
				...(filters?.requestedProcessingLevel
					? [
							{
								field: "requestedProcessingLevel",
								type: "exact",
								value: filters.requestedProcessingLevel,
							},
						]
					: []),
				...(filters?.gameAnalysisStatus
					? [
							{
								field: "gameAnalysisStatus",
								type: "exact",
								value: filters.gameAnalysisStatus,
							},
						]
					: []),
				...(filters?.country
					? [
							{
								field: "country",
								type: "exact",
								value: filters.country,
							},
						]
					: []),
				...(filters?.playerId
					? [
							{
								field: "playerId",
								type: "exact",
								value: filters.playerId,
							},
						]
					: []),
			],
			order: sort,
		},
		{ queryKey: ["discoverGamesTable"] }
	);

	useEffect(() => {
		if (gameAnalysisInEdit && data?.data.items) {
			setGameAnalysisInEdit(data.data.items.find((i: DiscoverGameResponse) => i.id === gameAnalysisInEdit.id));
		}
	}, [data?.data]);

	const { data: owners, isLoading: isOwnersLoading } = client.loadOwnerships.useQuery({});
	const { data: countries, isLoading: isCountriesLoading } = client.getDiscoverCountries.useQuery({});

	const isColumnVisible = (columnId: string): boolean => {
		return visibleColumns.hasOwnProperty(columnId) ? visibleColumns[columnId] : true;
	};

	const handleToggleColumnVisibility = (columnId: string) => {
		const columnState = visibleColumns;

		columnState[columnId] = !isColumnVisible(columnId);

		setVisibleColumns(columnState);
		setVisibleColumnsTick(!visibleColumnsTick);

		localStorage.setItem(columnsVisibilityKey, JSON.stringify(columnState));
	};

	const handleSetFilter = (key: string, value?: number | string) => {
		setFilters({ ...filters, [key]: value });
		setPagination({ ...pagination, pageIndex: 0 });
	};

	const handleClearFilter = (key: string) => {
		setFilters({ ...filters, [key]: undefined });
	};

	const handleSort = (newSort?: { field: string; direction: string }) => {
		setSort(newSort);
	};

	const handleDateTimeChange = (start: string, end: string) => {
		setDateTimeRange({ start: start, end: end });
	};

	const handleAnalysisRequestedAtDateTimeChange = (start: string, end: string) => {
		setAnalysisRequestedAtTimeRange({ start: start, end: end });
	};

	const generateColumnHeader =
		(columnName: string, label: string, hasSort: boolean = true, className?: string) =>
		() => (
			<div className={`tableHeader ${className}`}>
				<TableTextFilter
					value={(filterValues[columnName] || "").toString()}
					onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
					clear={() => {
						handleClearFilter(columnName);
						setFilterValues({ ...filterValues, [columnName]: undefined });
					}}
					onSubmit={(value: string) => handleSetFilter(columnName, value)}
					placeholder={label}
				/>
				{hasSort ? createSortArrows(columnName, sort, handleSort) : ""}
			</div>
		);

	const getPlayerNumber = (playerId: number, gamePlayers: DiscoverGameGamePlayerResponse[]) => {
		const player = gamePlayers.find((p) => p.player?.id === playerId);
		return player?.numberSign || "-";
	};

	const simpleColumns: SimpleColumn<DiscoverGameResponse>[] = [
		{
			id: "id",
			key: "id",
			header: generateColumnHeader("id", "ID", true, "tableHeader--id"),
			cell: (_, row) => <Link to={`/discover/game/${row.id}`}>{row.id}</Link>,
		},
		{
			id: "date",
			key: "date",
			header: () => (
				<div className="tableHeader tableHeader--calendar-left">
					<DateTimeRangeCalendar
						placeholder="Date"
						apiFormat
						onChange={handleDateTimeChange}
						start={dateTimeRange.start}
						end={dateTimeRange.end}
						locale={locale}
					/>
					{createSortArrows("date", sort, handleSort)}
				</div>
			),
			cell: (value) => formatDateTime(value as string | null),
		},
		{
			id: "country",
			key: "country",
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={(countries?.data || []).find((o) => o.value === filters?.country)}
						handleChange={(value) => handleSetFilter("country", value?.value)}
						placeholder="Country"
						items={countries?.data || []}
						isLoading={isCountriesLoading}
					/>
					{createSortArrows("country", sort, handleSort)}
				</div>
			),
			cell: (value, row: DiscoverGameResponse) => {
				const gameCountries = new Set<string | null | undefined>();

				gameCountries.add(row.home.club?.country?.name);
				gameCountries.add(row.away.club?.country?.name);
				gameCountries.add(row.home.team?.country?.name);
				gameCountries.add(row.away.team?.country?.name);

				const filtered = [...gameCountries].filter((c): c is string => c != null);
				return <div className="thinCell">{filtered.join(", ") || "-"}</div>;
			},
		},
		{
			id: "home",
			key: "home",
			header: () => (
				<div>
					<TableTextFilter
						key={1}
						value={(filterValues["homeClub"] || "").toString()}
						onChange={(value) => setFilterValues({ ...filterValues, ["homeClub"]: value })}
						clear={() => {
							handleClearFilter("homeClub");
							setFilterValues({ ...filterValues, ["homeClub"]: undefined });
						}}
						onSubmit={(value: string) => handleSetFilter("homeClub", value)}
						placeholder="Home club"
					/>
					<TableTextFilter
						key={2}
						value={(filterValues["homeTeam"] || "").toString()}
						onChange={(value) => setFilterValues({ ...filterValues, ["homeTeam"]: value })}
						clear={() => {
							handleClearFilter("homeTeam");
							setFilterValues({ ...filterValues, ["homeTeam"]: undefined });
						}}
						onSubmit={(value: string) => handleSetFilter("homeTeam", value)}
						placeholder="Home team"
					/>
				</div>
			),
			cell: (_, row) => (
				<div className="thinCell">
					{row.home?.club ? (
						<Link to={`/discover/club/${row.home.club.id}`}>
							<strong>{row.home.club.name}</strong> ({row.home.club.id})
						</Link>
					) : row.home?.clubName ? (
						<strong>
							<em>{row.home.clubName}</em>
						</strong>
					) : (
						"-"
					)}
					<br />
					{row.home?.team ? (
						<Link to={`/discover/team/${row.home.team.id}`}>
							{row.home.team.name} ({row.home.team.id})
						</Link>
					) : row.home?.teamName ? (
						<em>{row.home.teamName}</em>
					) : (
						"-"
					)}
				</div>
			),
		},
		{
			id: "away",
			key: "away",
			header: () => (
				<div>
					<TableTextFilter
						key={1}
						value={(filterValues["awayClub"] || "").toString()}
						onChange={(value) => setFilterValues({ ...filterValues, ["awayClub"]: value })}
						clear={() => {
							handleClearFilter("awayClub");
							setFilterValues({ ...filterValues, ["awayClub"]: undefined });
						}}
						onSubmit={(value: string) => handleSetFilter("awayClub", value)}
						placeholder="Away club"
					/>
					<TableTextFilter
						key={2}
						value={(filterValues["awayTeam"] || "").toString()}
						onChange={(value) => setFilterValues({ ...filterValues, ["awayTeam"]: value })}
						clear={() => {
							handleClearFilter("awayTeam");
							setFilterValues({ ...filterValues, ["awayTeam"]: undefined });
						}}
						onSubmit={(value: string) => handleSetFilter("awayTeam", value)}
						placeholder="Away team"
					/>
				</div>
			),
			cell: (_, row) => (
				<div className="thinCell">
					{row.away?.club ? (
						<Link to={`/discover/club/${row.away.club.id}`}>
							<strong>{row.away.club.name}</strong> ({row.away.club.id})
						</Link>
					) : row.away?.clubName ? (
						<strong>
							<em>{row.away.clubName}</em>
						</strong>
					) : (
						"-"
					)}
					<br />
					{row.away?.team ? (
						<Link to={`/discover/team/${row.away.team.id}`}>
							{row.away.team.name} ({row.away.team.id})
						</Link>
					) : row.away?.teamName ? (
						<strong>
							<em>{row.away.teamName}</em>
						</strong>
					) : (
						"-"
					)}
				</div>
			),
		},
		{
			id: "ageCategory",
			key: "ageCategory",
			header: () => (
				<div className="tableHeader tableHeader--select">
					<SelectComponent
						value={AgeCategoryOptions.find((o) => o.value === filters?.ageCategory)}
						handleChange={(value) => handleSetFilter("ageCategory", value?.value)}
						placeholder="Age Category"
						items={AgeCategoryOptions}
					/>
				</div>
			),
		},
		{
			id: "score",
			key: "score" as keyof DiscoverGameResponse,
			header: () => <div className="tableHeader">Score</div>,
			cell: (_, row) => <div style={{ textAlign: "center" }}>{`${row.home?.score || "-"} : ${row.away?.score || "-"}`}</div>,
		},
		{
			id: "teamSheet",
			key: "teamSheet" as keyof DiscoverGameResponse,
			header: () => <div className="tableHeader">Team Roster</div>,
			cell: (_, row) => (
				<div style={{ textAlign: "center", whiteSpace: "nowrap" }}>
					{renderBoolean(row.home?.isTeamsheetFilled)} ({row.home?.playersCount || 0}) | {renderBoolean(row.away?.isTeamsheetFilled)} (
					{row.away?.playersCount || 0})
				</div>
			),
		},
		{
			id: "video",
			key: "video" as keyof DiscoverGameResponse,
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={BooleanChoices.find((o) => o.value === filters?.hasVideo)}
						handleChange={(value) => handleSetFilter("hasVideo", value?.value)}
						placeholder="Has video"
						items={BooleanChoices}
					/>
				</div>
			),
			cell: (_, row) => renderBoolean(row.video !== null),
		},
		{
			id: "analysisProvider",
			key: "providerTag",
			header: () => (
				<SelectComponent
					value={providerTagOptions.find((o) => o.value === filters?.providerTag)}
					handleChange={(value) => handleSetFilter("providerTag", value?.value)}
					placeholder="Analysis Provider"
					items={providerTagOptions}
				/>
			),
			cell: (value, row) => (
				<span onClick={() => setGameAnalysisInEdit(row)} className="cell-linked">
					{row.providerTag || "NONE"}
				</span>
			),
		},
		{
			id: "gameAnalysisStatus",
			key: "gameAnalysisStatus",
			header: () => (
				<SelectComponent
					value={GameAnalysisStatusEnumChoices.find((o) => o.value === filters?.gameAnalysisStatus)}
					handleChange={(value) => handleSetFilter("gameAnalysisStatus", value?.value)}
					placeholder="Game Analysis Status"
					items={GameAnalysisStatusEnumChoices}
				/>
			),
			cell: (value, row) => <span>{row.currentGameAnalysis?.status || "-"}</span>,
		},
		{
			id: "isVip",
			key: "isVip",
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={BooleanChoices.find((o) => o.value === filters?.isVip)}
						handleChange={(value) => handleSetFilter("isVip", value?.value)}
						placeholder="Vip"
						items={BooleanChoices}
					/>
				</div>
			),
			cell: (value, row) =>
				canEditVip ? (
					<SelectComponent
						value={BooleanChoices.find((o) => o.value === (row.isVip ? "true" : "false"))}
						handleChange={(option) => {
							if (option?.value !== undefined) {
								patchDiscoverGameMutation.mutate({
									id: row.id,
									body: { id: row.id, requestedFields: ["isVip"], isVip: option.value === "true" },
								});
							}
						}}
						items={BooleanChoices}
						isCleareable={false}
						isDisabled={patchDiscoverGameMutation.isLoading}
						formatOptionLabel={renderBooleanChoiceLabel}
					/>
				) : (
					renderBoolean(row.isVip)
				),
		},
		{
			id: "isHidden",
			key: "isHidden",
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={BooleanChoices.find((o) => o.value === filters?.isHidden)}
						handleChange={(value) => handleSetFilter("isHidden", value?.value)}
						placeholder="Hidden"
						items={BooleanChoices}
					/>
				</div>
			),
			cell: (value, row) => renderBoolean(row.isHidden),
		},
		{
			id: "isHiddenForScout",
			key: "isHiddenForScout",
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={BooleanChoices.find((o) => o.value === filters?.isHiddenForScout)}
						handleChange={(value) => handleSetFilter("isHiddenForScout", value?.value)}
						placeholder="Hidden For Scout"
						items={BooleanChoices}
					/>
				</div>
			),
			cell: (value, row) => renderBoolean(row.isHiddenForScout),
		},
		{
			id: "owners",
			key: "owners",
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={(owners?.data.data || []).find((o) => o.value === filters?.owner)}
						handleChange={(value) => handleSetFilter("owner", value?.value)}
						placeholder="Owner"
						items={(owners?.data.data || []) as { value: number | string; label: string; type?: string }[]}
						isLoading={isOwnersLoading}
					/>
				</div>
			),
			cell: (value, row) => row.owners?.map((o) => o.name).join(", "),
		},
		{
			id: "isDiscover",
			key: "isDiscover",
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={isDiscoverOptions.find((o) => o.value === filters?.isDiscover)}
						handleChange={(value) => handleSetFilter("isDiscover", value?.value)}
						placeholder="Is Discover"
						items={isDiscoverOptions}
					/>
				</div>
			),
			cell: (value) => (value ? "Discover" : "Elite"),
		},
		{
			id: "requestedProcessingLevel",
			key: "requestedProcessingLevel",
			header: () => (
				<div className="tableHeader">
					<SelectComponent
						value={processingLevelOptions.find((o) => o.value === filters?.requestedProcessingLevel)}
						handleChange={(value) => handleSetFilter("requestedProcessingLevel", value?.value)}
						placeholder="Requsted Processing Level"
						items={processingLevelOptions}
					/>
				</div>
			),
			cell: (value: string | null) => ucFirst(value) || "-",
		},
		{
			id: "analysisRequestedAt",
			key: "analysisRequestedAt",
			header: () => (
				<div className="tableHeader">
					<DateTimeRangeCalendar
						placeholder="Analysis Requested At"
						apiFormat
						onChange={handleAnalysisRequestedAtDateTimeChange}
						start={analysisRequestedAtTimeRange.start}
						end={analysisRequestedAtTimeRange.end}
						locale={locale}
					/>
					{createSortArrows("analysisRequestedAt", sort, handleSort)}
				</div>
			),
			cell: (value, row) => formatDate(value as string | null),
		},
	];

	if (filters?.playerId) {
		simpleColumns.unshift({
			id: "playerId",
			key: "playerId" as keyof DiscoverGameResponse,
			header: () => (
				<div className="tableHeader" style={{ cursor: "default" }}>
					Player number
				</div>
			),
			cell: (_, row) => getPlayerNumber(filters.playerId as number, row.home?.gamePlayers ?? row.away?.gamePlayers ?? []),
		});
	}

	const preHiddenColumns = Object.keys(columnVisibility);

	return (
		<>
			<div className="column-visibility">
				<span className="column-visibility-handle">
					<span className="icon-new icon-grey icon-eye" onClick={() => setShowColumnsModal(true)}></span>
				</span>
				<Modal show={showColumnsModal} onClose={() => setShowColumnsModal(false)} size="sm" title="Columns visibility">
					{simpleColumns
						.filter((column) => !preHiddenColumns.includes(column.key))
						.map((c, index) => (
							<div
								key={`column-${index}`}
								className={`column-visibility-item ${isColumnVisible(c.id || "") ? "is-active" : ""}`}
								onClick={(e) => handleToggleColumnVisibility(c.id || "")}
							>
								{humanizeLabel(c.id || "")}
							</div>
						))}
				</Modal>
			</div>
			<DatagridV2
				isLoading={isLoading}
				simpleColumns={simpleColumns}
				data={data?.data.items ?? []}
				pageCount={data?.data?.pages ?? 1}
				pagination={pagination}
				setPagination={setPagination}
				noResults={_("datatables.zero_records")}
				columnVisibility={visibleColumns}
			/>
			{gameAnalysisInEdit !== null && (
				<Modal
					title={`Game Analysis for game #${gameAnalysisInEdit?.id}`}
					onClose={() => setGameAnalysisInEdit(null)}
					show={gameAnalysisInEdit !== null}
					size="lg"
					position="top"
				>
					<DiscoverGameAnalysisDialog
						gameData={gameAnalysisInEdit}
						invalidationQueryKey="discoverGamesTable"
					/>
				</Modal>
			)}
		</>
	);
}
